import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type PurchaseRow, type PurchaseDetail } from '../api';
import { ReceiptBadge, InvoiceBadge } from '../components/StatusBadge';

function fmtMoney(n: number, currency: string | null): string {
  return `${n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ' ' + currency : ''}`;
}
function fmtDate(d: string | false): string {
  if (!d) return '—';
  const p = String(d).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(d);
}

type Toast = { kind: 'ok' | 'err'; text: string } | null;
type Filter = 'all' | 'missing';

export default function Achats() {
  const [items, setItems] = useState<PurchaseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PurchaseDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const nav = useNavigate();

  function reload() {
    setLoading(true);
    setError(null);
    return api.listPurchases().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    api.getPurchase(selectedId).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }, [selectedId]);

  const visible = items.filter((m) => (filter === 'missing' ? m.receipt === 'missing' : true));
  const missingCount = items.filter((m) => m.receipt === 'missing').length;

  async function askAssistant() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name}`).join('\n');
    const prefix =
      `Je regarde la facture fournisseur Odoo **${detail.name}** (account.move id ${detail.id}, réf ${detail.ref || '—'}).\n` +
      `- Fournisseur : ${detail.partner ?? '—'} · Total : ${fmtMoney(detail.amount_total, detail.currency)}\n` +
      `- Réception : ${detail.receptions.map((r) => `${r.name} (${r.state})`).join(', ') || 'aucune'}\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    try {
      const { conversation, existing } = await api.openDocConversation(`account.move:${detail.id}`, `Facture fourn. ${detail.name}`);
      nav('/chat', { state: existing ? { conv: conversation.id } : { conv: conversation.id, draft: prefix } });
    } catch (e: any) { setToast({ kind: 'err', text: e.message }); }
  }

  return (
    <div className="devis-page">
      <div className="page-toolbar">
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>

      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}

      <div className="ventes-toolbar">
        <div className="suivi-filters">
          <button className={`suivi-filter ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Toutes</button>
          <button className={`suivi-filter ${filter === 'missing' ? 'active' : ''}`} onClick={() => setFilter('missing')}>
            Marchandise non reçue{missingCount ? ` (${missingCount})` : ''}
          </button>
        </div>
      </div>

      <div className="devis-body">
        <div className="devis-list">
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && visible.length === 0 && <div className="devis-empty">Aucune facture fournisseur.</div>}
          {!loading && !error && visible.length > 0 && (
            <table>
              <thead>
                <tr><th>N° / Réf.</th><th>Fournisseur</th><th className="num">Montant</th><th>Date</th><th>État</th><th>Réception</th></tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr key={m.id} className={m.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(m.id)}>
                    <td className="mono">{m.name}{m.ref && m.name !== m.ref ? <span className="ref-sub"> · {m.ref}</span> : ''}</td>
                    <td>{m.partner ?? '—'}</td>
                    <td className="num">{fmtMoney(m.amount_total, m.currency)}</td>
                    <td>{fmtDate(m.invoice_date)}</td>
                    <td><InvoiceBadge state={m.state} payment={m.payment_state} /></td>
                    <td><ReceiptBadge receipt={m.receipt} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedId != null && (
          <aside className="devis-detail">
            <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
            {detailLoading && <div className="devis-empty">Chargement…</div>}
            {detail && (
              <>
                <h2>{detail.name}</h2>
                <div className="devis-meta">
                  <div><span>Fournisseur</span><strong>{detail.partner ?? '—'}</strong></div>
                  {detail.ref && <div><span>Réf. fournisseur</span><strong>{detail.ref}</strong></div>}
                  <div><span>État</span><strong><InvoiceBadge state={detail.state} payment={detail.payment_state} /></strong></div>
                  <div><span>Réception</span><strong><ReceiptBadge receipt={detail.receipt} /></strong></div>
                  <div><span>Date</span><strong>{fmtDate(detail.invoice_date)}</strong></div>
                </div>

                <table className="devis-lines">
                  <thead><tr><th>Article</th><th className="num">Qté</th><th className="num">P.U.</th><th className="num">Sous-total</th></tr></thead>
                  <tbody>
                    {detail.lines.map((l) => (
                      <tr key={l.id}>
                        <td>{l.product ?? l.name}</td>
                        <td className="num">{l.qty}</td>
                        <td className="num">{fmtMoney(l.price_unit, null)}</td>
                        <td className="num">{fmtMoney(l.subtotal, null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="devis-totals">
                  <div><span>Hors taxe</span><strong>{fmtMoney(detail.amount_untaxed, detail.currency)}</strong></div>
                  <div><span>Taxes</span><strong>{fmtMoney(detail.amount_tax, detail.currency)}</strong></div>
                  <div className="total"><span>Total</span><strong>{fmtMoney(detail.amount_total, detail.currency)}</strong></div>
                </div>

                <div className="devis-meta" style={{ marginTop: 8 }}>
                  <div><span>Réceptions liées</span><strong>{detail.receptions.length ? detail.receptions.map((r) => `${r.name} (${r.state})`).join(', ') : '—'}</strong></div>
                </div>

                {detail.receipt === 'missing' && (
                  <div className="devis-warn">⚠️ Marchandise non reçue : cette facture contient des produits stockables mais aucune réception validée — le stock n'a pas été incrémenté.</div>
                )}
                {detail.receipt === 'service' && (
                  <div className="devis-meta"><div><span></span><strong style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>Facture de service/frais : aucune réception attendue.</strong></div></div>
                )}

                <div className="devis-actions">
                  <a className="devis-odoo" href={`/api/purchases/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
                  <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
                  <button className="devis-ask" onClick={askAssistant}>💬 Demander une modification</button>
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
